/**
 * PrintFlow Material Advisor — floating AI chat widget
 *
 * Helps visitors pick the right 3D printing technology + material by asking
 * about their application, then hands off to the quote form with the
 * recommendation pre-selected (?tech=...&material=...).
 *
 * Config (set by snippets/printflow-advisor.liquid before this script):
 *   window.PF_ADVISOR_CONFIG = {
 *     apiUrl:   'https://your-backend.up.railway.app',   // no trailing slash
 *     quoteUrl: '/pages/quote',                          // where the quote form lives
 *     accent:   '#6366f1',                               // optional brand colour
 *   }
 *
 * Open triggers (besides the floating bubble): any element with a
 * data-pf-advisor attribute, or any <a href="#material-advisor">. Example:
 *   <a href="#material-advisor">Not sure which material? Ask our advisor →</a>
 */
(function () {
  'use strict';
  var CFG = window.PF_ADVISOR_CONFIG || {};
  var API = (CFG.apiUrl || '').replace(/\/$/, '');
  var QUOTE_URL = CFG.quoteUrl || '/pages/quote';
  var ACCENT = CFG.accent || '#6366f1';
  // CTA label template — [REC] is replaced with e.g. "FDM · ASA"
  var CTA_TEXT = CFG.ctaText || 'Get an instant quote with [REC] →';
  // Shopify Inbox integration: we hide Inbox's own launcher so there's ONE
  // chat bubble, and "Talk to a human" reveals + opens Inbox. If Inbox isn't
  // on the page (app removed / markup changed), we fall back to the contact page.
  var INBOX_TAG = CFG.inboxSelector || 'inbox-online-store-chat';
  var CONTACT_URL = CFG.contactUrl || '/pages/contact';
  if (!API) { console.warn('[pf-advisor] PF_ADVISOR_CONFIG.apiUrl not set'); return; }

  // If the visitor already switched to live chat this session, stay out of the
  // way: leave Shopify Inbox visible and don't render the advisor at all.
  var escalated = false;
  try { escalated = sessionStorage.getItem('pfa_human') === '1'; } catch (e) {}
  if (escalated) return;

  // Hide Shopify Inbox's launcher while the advisor owns the corner.
  // Inbox can spawn TWO instances (one inside div#shopify-chat, one on body)
  // and the #ShopifyChat one wins specificity fights — so hide by CSS (both
  // selectors) AND by inline style on the element's wrapper, and keep a
  // MutationObserver to catch instances that attach later.
  var inboxHideStyle = document.createElement('style');
  inboxHideStyle.textContent = INBOX_TAG + ', #shopify-chat {display:none !important;}';
  document.head.appendChild(inboxHideStyle);

  function inboxHosts() {
    var hosts = [];
    document.querySelectorAll(INBOX_TAG).forEach(function (el) {
      hosts.push(el);
      if (el.parentElement && el.parentElement.tagName === 'DIV') hosts.push(el.parentElement);
    });
    return hosts;
  }
  function hideInbox() {
    inboxHosts().forEach(function (el) { el.style.setProperty('display', 'none', 'important'); });
  }
  hideInbox();
  var inboxObserver = new MutationObserver(hideInbox);
  inboxObserver.observe(document.body, { childList: true, subtree: false });

  var GREETING = "Hi! I can help you pick the right 3D printing technology and material. What are you making, and what will it be used for?";
  var TECH_LABELS = { fdm: 'FDM', sla: 'SLA Resin', sls: 'SLS Nylon' };

  // Conversation state — survives page navigation within the tab
  var state = { messages: [], rec: null, busy: false };
  try {
    var saved = sessionStorage.getItem('pfa_chat');
    if (saved) { var s = JSON.parse(saved); state.messages = s.messages || []; state.rec = s.rec || null; }
  } catch (e) {}

  function persist() {
    try { sessionStorage.setItem('pfa_chat', JSON.stringify({ messages: state.messages, rec: state.rec })); } catch (e) {}
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Escape, then render the model's **bold** as real bold
  function fmt(s) {
    return esc(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  }

  // ── Styles ────────────────────────────────────────────────────────────────────
  var css = [
    '#pfa-bubble{position:fixed;bottom:22px;right:22px;z-index:99990;width:58px;height:58px;border-radius:50%;background:' + ACCENT + ';color:#fff;border:none;cursor:pointer;box-shadow:0 6px 24px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;transition:transform .15s;}',
    '#pfa-bubble:hover{transform:scale(1.08);}',
    '#pfa-bubble svg{width:28px;height:28px;}',
    '#pfa-panel{position:fixed;bottom:92px;right:22px;z-index:99991;width:370px;max-width:calc(100vw - 24px);height:520px;max-height:calc(100vh - 120px);background:#fff;border-radius:16px;box-shadow:0 12px 48px rgba(0,0,0,.28);display:none;flex-direction:column;overflow:hidden;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;}',
    '#pfa-panel.open{display:flex;}',
    '#pfa-head{background:' + ACCENT + ';color:#fff;padding:14px 16px;display:flex;align-items:center;gap:10px;}',
    '#pfa-head .t{font-weight:700;font-size:14px;}#pfa-head .s{font-size:11px;opacity:.85;}',
    '#pfa-close{margin-left:auto;background:none;border:none;color:#fff;font-size:20px;cursor:pointer;line-height:1;padding:4px;}',
    '#pfa-msgs{flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:10px;background:#f7f7f8;}',
    '.pfa-m{max-width:85%;padding:9px 12px;border-radius:14px;font-size:13.5px;line-height:1.55;white-space:pre-wrap;word-wrap:break-word;}',
    '.pfa-m.bot{background:#fff;border:1px solid #e5e7eb;color:#1f2937;align-self:flex-start;border-bottom-left-radius:4px;}',
    '.pfa-m.user{background:' + ACCENT + ';color:#fff;align-self:flex-end;border-bottom-right-radius:4px;}',
    '.pfa-m.err{background:#fef2f2;border:1px solid #fecaca;color:#b91c1c;align-self:flex-start;}',
    '#pfa-cta{align-self:flex-start;background:#16a34a;color:#fff;text-decoration:none;font-size:13px;font-weight:700;padding:10px 14px;border-radius:10px;display:inline-block;}',
    '#pfa-typing{align-self:flex-start;font-size:12px;color:#9ca3af;padding:2px 6px;}',
    '#pfa-form{display:flex;gap:8px;padding:10px;border-top:1px solid #e5e7eb;background:#fff;}',
    '#pfa-in{flex:1;border:1px solid #d1d5db;border-radius:10px;padding:9px 12px;font-size:13.5px;outline:none;font-family:inherit;}',
    '#pfa-in:focus{border-color:' + ACCENT + ';}',
    '#pfa-send{background:' + ACCENT + ';color:#fff;border:none;border-radius:10px;padding:0 14px;font-size:14px;cursor:pointer;font-weight:600;}',
    '#pfa-send:disabled{opacity:.5;cursor:default;}',
    '#pfa-foot{display:flex;background:#fff;}',
    '#pfa-reset,#pfa-human{background:none;border:none;color:#9ca3af;font-size:11px;cursor:pointer;padding:6px;text-align:center;flex:1;}',
    '#pfa-human{color:#6b7280;font-weight:600;}',
    '@media(max-width:480px){#pfa-panel{right:8px;left:8px;width:auto;bottom:84px;height:70vh;}}',
  ].join('\n');
  var styleEl = document.createElement('style');
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  // ── DOM ─────────────────────────────────────────────────────────────────────────
  var bubble = document.createElement('button');
  bubble.id = 'pfa-bubble';
  bubble.setAttribute('aria-label', 'Material Advisor chat');
  bubble.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';

  var panel = document.createElement('div');
  panel.id = 'pfa-panel';
  panel.innerHTML =
    '<div id="pfa-head">' +
      '<div><div class="t">Material Advisor</div><div class="s">Find the right technology &amp; material</div></div>' +
      '<button id="pfa-close" aria-label="Close">&times;</button>' +
    '</div>' +
    '<div id="pfa-msgs"></div>' +
    '<form id="pfa-form">' +
      '<input id="pfa-in" type="text" maxlength="2000" placeholder="e.g. a bracket for my dirt bike" autocomplete="off">' +
      '<button id="pfa-send" type="submit">Send</button>' +
    '</form>' +
    '<div id="pfa-foot">' +
      '<button id="pfa-reset" type="button">Start over</button>' +
      '<button id="pfa-human" type="button">&#128172; Talk to a human</button>' +
    '</div>';

  document.body.appendChild(bubble);
  document.body.appendChild(panel);

  var msgsEl = panel.querySelector('#pfa-msgs');
  var inEl   = panel.querySelector('#pfa-in');
  var sendEl = panel.querySelector('#pfa-send');

  // ── Rendering ─────────────────────────────────────────────────────────────────
  function quoteHref() {
    if (!state.rec) return QUOTE_URL;
    var qs = new URLSearchParams();
    if (state.rec.technology) qs.set('tech', state.rec.technology);
    if (state.rec.material)   qs.set('material', state.rec.material);
    return QUOTE_URL + (QUOTE_URL.indexOf('?') >= 0 ? '&' : '?') + qs.toString();
  }

  function render() {
    var html = '<div class="pfa-m bot">' + esc(GREETING) + '</div>';
    state.messages.forEach(function (m) {
      html += '<div class="pfa-m ' + (m.role === 'user' ? 'user' : 'bot') + '">' + (m.role === 'user' ? esc(m.content) : fmt(m.content)) + '</div>';
    });
    if (state.rec) {
      var label = (TECH_LABELS[state.rec.technology] || state.rec.technology.toUpperCase()) +
        (state.rec.material ? ' · ' + state.rec.material.toUpperCase() : '');
      html += '<a id="pfa-cta" href="' + esc(quoteHref()) + '">' + esc(CTA_TEXT.replace('[REC]', label)).replace(/→/g, '&rarr;') + '</a>';
    }
    if (state.busy) html += '<div id="pfa-typing">Advisor is thinking…</div>';
    msgsEl.innerHTML = html;
    msgsEl.scrollTop = msgsEl.scrollHeight;
    sendEl.disabled = state.busy;
  }

  function showError(text) {
    var div = document.createElement('div');
    div.className = 'pfa-m err';
    div.textContent = text;
    msgsEl.appendChild(div);
    msgsEl.scrollTop = msgsEl.scrollHeight;
  }

  // ── Chat flow ──────────────────────────────────────────────────────────────────
  async function send(text) {
    if (state.busy) return;
    state.messages.push({ role: 'user', content: text });
    state.busy = true;
    persist();
    render();

    try {
      var res = await fetch(API + '/api/advisor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: state.messages }),
        signal: AbortSignal.timeout(30000),
      });
      var data = await res.json().catch(function () { return {}; });
      state.busy = false;
      if (!res.ok) {
        if (data.code === 'too_long') { reset(); return; }
        render();
        showError(data.error || 'Something went wrong — please try again.');
        return;
      }
      state.messages.push({ role: 'assistant', content: data.reply });
      if (data.recommendation) state.rec = data.recommendation;
      persist();
      render();
    } catch (e) {
      state.busy = false;
      render();
      showError('Could not reach the advisor — check your connection and try again.');
    }
  }

  function reset() {
    state = { messages: [], rec: null, busy: false };
    persist();
    render();
  }

  // ── Hand off to Shopify Inbox (live chat with the team) ────────────────────
  function escalate() {
    var inbox = document.querySelector(INBOX_TAG);
    if (!inbox) { window.location.href = CONTACT_URL; return; }

    var note = document.createElement('div');
    note.className = 'pfa-m bot';
    note.textContent = 'Connecting you to our team… 👋';
    msgsEl.appendChild(note);
    msgsEl.scrollTop = msgsEl.scrollHeight;

    setTimeout(function () {
      try { sessionStorage.setItem('pfa_human', '1'); } catch (e) {}
      // Reveal Inbox, retire the advisor for this session
      inboxObserver.disconnect();
      inboxHideStyle.remove();
      inboxHosts().forEach(function (el) { el.style.removeProperty('display'); });
      panel.style.display = 'none';
      bubble.style.display = 'none';
      // Open the Inbox chat window directly (open shadow DOM → launcher button).
      // Prefer the app-embed instance (inside div#shopify-chat) over strays.
      try {
        var candidates = [];
        var embedded = document.querySelector('div > ' + INBOX_TAG);
        if (embedded) candidates.push(embedded);
        document.querySelectorAll(INBOX_TAG).forEach(function (el) {
          if (candidates.indexOf(el) < 0) candidates.push(el);
        });
        for (var i = 0; i < candidates.length; i++) {
          var btn = candidates[i].shadowRoot && candidates[i].shadowRoot.querySelector('button');
          if (btn) { btn.click(); break; }
        }
      } catch (e) { /* launcher is visible either way */ }
    }, 700);
  }

  // ── Events ───────────────────────────────────────────────────────────────────────────
  function openPanel() { panel.classList.add('open'); render(); setTimeout(function () { inEl.focus(); }, 50); }
  function closePanel() { panel.classList.remove('open'); }
  function togglePanel() { panel.classList.contains('open') ? closePanel() : openPanel(); }

  bubble.addEventListener('click', togglePanel);
  panel.querySelector('#pfa-close').addEventListener('click', closePanel);
  panel.querySelector('#pfa-reset').addEventListener('click', reset);
  panel.querySelector('#pfa-human').addEventListener('click', escalate);
  panel.querySelector('#pfa-form').addEventListener('submit', function (e) {
    e.preventDefault();
    var text = inEl.value.trim();
    if (!text) return;
    inEl.value = '';
    send(text);
  });

  // "Little link" triggers anywhere on the site
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-pf-advisor], a[href="#material-advisor"]');
    if (el) { e.preventDefault(); openPanel(); }
  });
})();
